// ADDED: import hooks we'll use to measure the page content top
import { useEffect, useRef } from 'react';

import { Outlet, useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import ScrollToTop from '../ScrollToTop';

const Layout = () => {
  const location = useLocation();

  // ADDED: ref to the real page-content container (.app-content)
  const mainRef = useRef<HTMLElement | null>(null);

  // ADDED: keep the yellow line (CSS var --content-top) in sync with layout
  useEffect(() => {
    const setContentTopVar = () => {
      if (!mainRef.current) return;
      // Measure the *viewport* Y of the start of your page content
      const top = Math.max(0, Math.round(mainRef.current.getBoundingClientRect().top));
      document.documentElement.style.setProperty('--content-top', `${top}px`);
      // Optional trace:
      // console.debug('[debug-safe] --content-top =', top);
    };

    // Schedule measure for next frames so fonts/navbars have laid out
    const schedule = () =>
      requestAnimationFrame(() => {
        setContentTopVar();
        requestAnimationFrame(setContentTopVar);
      });

    schedule();

    // Update on window resizes
    window.addEventListener('resize', schedule);

    // Update after web fonts finish loading (if supported)
    // (kept optional and non-blocking)
    (document as any).fonts?.ready?.then(schedule).catch(() => {});

    // React to size changes of the content container itself (ref may be null on first run)
    const ro = new ResizeObserver(schedule);
    const rafId = requestAnimationFrame(() => {
      if (mainRef.current) ro.observe(mainRef.current);
    });

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', schedule);
      ro.disconnect();
    };
    // Re-run on route changes (new pages often change header heights)
  }, [location.pathname]);

  // Route-aware: compact layout for auth pages
  const isAuthPage = location.pathname === '/login' || location.pathname === '/signup';

  return (
    <div className={`bg-gray-50 ${isAuthPage ? 'flex flex-col' : 'flex flex-col min-h-screen'}`}>
      <ScrollToTop />
      <Navbar />

      {/* Single source of truth for top offset: .app-content (defined in index.css) */}
      <main
        role="main"
        ref={mainRef}
        className={`${isAuthPage ? '' : 'app-content container mx-auto px-4 py-8 flex-grow'}`}
        // Fill-to-footer only on *non-auth* pages (compositing fixes are in .app-content CSS class)
        style={!isAuthPage ? { minHeight: 'calc(100vh - var(--full-header-height))' } : undefined}
      >
        <div className={isAuthPage ? '' : 'min-h-full'}>
          <Outlet />
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Layout;